import { ReactNode, useState } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { useQuery } from '@apollo/client/react';
import { Button, Link, Paper, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography } from '@mui/material';
import { Add } from '@mui/icons-material';
import LoadingSpinner from '../components/LoadingSpinner';
import { MainContentPanel } from '../components/MainContentPanel';
import { numberFormatted } from '../Formatters';
import { GuidePriceModel, SubscriptionModel } from '../interfaces';
import { GET_SUBSCRIPTIONS, GET_SUBSCRIPTION_USAGE } from './subscriptions.graphql';
import { GET_PRICE } from '../pdf/pdf.graphql';
import { GET_HISTORICAL_PRICE } from '../historical/historical.graphql';
import { SubscriptionDialog, SubscriptionUsage } from './SubscriptionDialog';
import { describeContent } from './content';
import { GuideStatus, SubscriptionStatus, kr } from './status';

export function Overview() {
  const { data: subscriptionData, loading } = useQuery<{ subscriptions: SubscriptionModel[] }>(GET_SUBSCRIPTIONS);
  const { data: usageData } = useQuery<{ usage: SubscriptionUsage[] }>(GET_SUBSCRIPTION_USAGE, { fetchPolicy: 'cache-and-network' });
  const { data: guideData } = useQuery<{ pdf: GuidePriceModel }>(GET_PRICE);
  const { data: historicalData } = useQuery<{ historical: GuidePriceModel }>(GET_HISTORICAL_PRICE);
  const [creating, setCreating] = useState(false);

  const subscriptions = subscriptionData?.subscriptions ?? [];
  const usage = usageData?.usage ?? [];

  return (
    <MainContentPanel>
      <Stack direction="row" sx={{ justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1, mb: 2 }}>
        <Typography variant="h5">Produkter</Typography>
        <Button variant="contained" startIcon={<Add />} disabled={!subscriptionData} onClick={() => setCreating(true)} sx={{ whiteSpace: 'nowrap' }}>
          Nytt abonnement
        </Button>
      </Stack>
      {loading && <LoadingSpinner />}
      {subscriptionData && (
        <Stack spacing={3}>
          <ProductTable
            title="Abonnement"
            columns={[
              'Navn',
              'Maconomy-nr.',
              { label: 'Innhold', from: 'md' },
              { label: '1 bruker', right: true, from: 'sm' },
              { label: 'Ekstra bruker', right: true, from: 'md' },
              { label: 'Lisenser', right: true, from: 'md' },
              { label: 'Status', from: 'sm' },
            ]}
          >
            {inHierarchyOrder(subscriptions).map((x) => {
              const isPart = x.parentId != null;
              const inUse = usage.find((u) => u.subscriptionId === x.bksId);
              return (
                <LinkedRow key={x.bksId} to={`/product/${x.bksId}`}>
                  <TableCell sx={{ pl: isPart ? 4 : undefined }}>
                    <RowLink to={`/product/${x.bksId}`} secondary={isPart}>
                      {x.name}
                    </RowLink>
                  </TableCell>
                  <TableCell>{x.number}</TableCell>
                  <TableCell sx={shownFrom('md')}>{describeContent(x.content, x.product)}</TableCell>
                  <TableCell align="right" sx={shownFrom('sm')}>
                    {kr(x.price?.singleUser)}
                  </TableCell>
                  <TableCell align="right" sx={shownFrom('md')}>
                    {kr(x.price?.extraUser)}
                  </TableCell>
                  <TableCell align="right" sx={shownFrom('md')}>
                    {numberFormatted(inUse?.licenses) ?? 0}
                  </TableCell>
                  <TableCell sx={shownFrom('sm')}>
                    <SubscriptionStatus subscription={x} />
                  </TableCell>
                </LinkedRow>
              );
            })}
          </ProductTable>
          <ProductTable
            title="Enkeltanvisning"
            columns={['Versjon', 'Maconomy-nr.', { label: 'Pris', right: true, from: 'sm' }, { label: 'Status', from: 'sm' }]}
          >
            <LinkedRow to="/product/guide/gjeldende">
              <TableCell>
                <RowLink to="/product/guide/gjeldende">Gjeldende</RowLink>
              </TableCell>
              <TableCell sx={{ color: 'text.secondary' }}>Dokumentnr.</TableCell>
              <TableCell align="right" sx={shownFrom('sm')}>
                {kr(guideData?.pdf.price)}
              </TableCell>
              <TableCell sx={shownFrom('sm')}>
                <GuideStatus guide={guideData?.pdf} />
              </TableCell>
            </LinkedRow>
            <LinkedRow to="/product/guide/historisk">
              <TableCell>
                <RowLink to="/product/guide/historisk">Historisk</RowLink>
              </TableCell>
              <TableCell>BFS HIST</TableCell>
              <TableCell align="right" sx={shownFrom('sm')}>
                {kr(historicalData?.historical.price)}
              </TableCell>
              <TableCell sx={shownFrom('sm')}>
                <GuideStatus guide={historicalData?.historical} />
              </TableCell>
            </LinkedRow>
          </ProductTable>
        </Stack>
      )}
      {creating && subscriptionData && <SubscriptionDialog subscriptions={subscriptions} onClose={() => setCreating(false)} />}
    </MainContentPanel>
  );
}

function inHierarchyOrder(subscriptions: SubscriptionModel[]): SubscriptionModel[] {
  const byId = [...subscriptions].sort((a, b) => a.bksId - b.bksId);
  const roots = byId.filter((x) => x.parentId == null || !byId.some((p) => p.bksId === x.parentId));
  return roots.flatMap((root) => [root, ...byId.filter((x) => x.parentId === root.bksId)]);
}

const shownFrom = (breakpoint: 'sm' | 'md') => ({ display: { xs: 'none', [breakpoint]: 'table-cell' } });

type Column = string | { label: string; right?: boolean; from?: 'sm' | 'md' };

function ProductTable({ title, columns, children }: { title: string; columns: Column[]; children: ReactNode }) {
  return (
    <Stack spacing={1}>
      <Typography variant="h6">{title}</Typography>
      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              {columns.map((c) => {
                const column = typeof c === 'string' ? { label: c } : c;
                return (
                  <TableCell key={column.label} align={column.right ? 'right' : undefined} sx={column.from ? shownFrom(column.from) : undefined}>
                    {column.label}
                  </TableCell>
                );
              })}
            </TableRow>
          </TableHead>
          <TableBody>{children}</TableBody>
        </Table>
      </TableContainer>
    </Stack>
  );
}

function LinkedRow({ to, children }: { to: string; children: ReactNode }) {
  const navigate = useNavigate();
  return (
    <TableRow hover onClick={() => navigate(to)} sx={{ cursor: 'pointer' }}>
      {children}
    </TableRow>
  );
}

/** A real link too, so it can be opened in a new tab. */
function RowLink({ to, secondary, children }: { to: string; secondary?: boolean; children: ReactNode }) {
  return (
    <Link component={RouterLink} to={to} underline="hover" color={secondary ? 'text.secondary' : 'inherit'} onClick={(e) => e.stopPropagation()}>
      {children}
    </Link>
  );
}
